import React, { Component, ContextType } from "react";
import classnames from "classnames";
import equal from "fast-deep-equal/es6";
import { observable, toJS } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IDashboardLayout } from "@formant/core/model/IDashboardLayout";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import { IFullScreenLayout } from "@formant/core/model/IFullScreenLayout";
import { ILayoutTypeMap } from "@formant/core/model/ILayoutTypeMap";
import { INodeGraph } from "@formant/core/model/IModuleNode";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import { IView } from "@formant/core/model/IView";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { LayoutName, LayoutType } from "@formant/core/model/LayoutType";
import { Timeout } from "@formant/core/model/Timeout";
import { Uuid } from "@formant/core/model/Uuid";
import {
    appStoreBase,
    defaultStoreId,
    EventSidebar as EventSidebarV2,
    FormantClientContext,
} from "@formant/rectangle-sdk";
import { Editor, EditorMode, useBaseEditorStore } from "@formant/views-editor";

import { setTrackingContext, track } from "../../analytics";
import { deviceClient } from "../../clients";
import {
    IViewContext,
    ViewContext,
} from "../../context/TeleopContext/ViewContext";
import { UrlParamService } from "../../features/urlParams";
import { UrlParamSyncHelper } from "../../features/urlParams/components/UrlParamSyncHelper";
import { SnackbarPosition } from "../../store/IAppStore";
import {
    appStore,
    authStore,
    deviceConfigurationStore,
    deviceStore,
    groupStore,
    observeStore,
    operateStore,
    organizationStore,
    routerStore,
    viewStore,
} from "../../stores";
import { dirtyDiff } from "../../utils/diffTools";
import { hashObservable } from "../../utils/hashObservable";
import * as SelfServiceViewUpdater from "../../utils/selfServiceViewUpdater";
import { viewConflictMessage } from "../../utils/viewConflictMessage";
import { Hotkey } from "../common/hotkey/Hotkey";
import { toggleStreamFromFilter } from "../common/ModuleAdd";
import { RestrictedComponent } from "../common/RestrictedComponent";
import { Switch } from "../common/Switch";
import { TeleopViewSelectField } from "../configuration/TeleopViewSelectField";
import { DataRetentionBanner } from "../DataRetentionBanner";
import { Inspector } from "../Inspector";
import { Label } from "../JsonSchemaForm/Label";
import { ListButton } from "../JsonSchemaForm/ListButton";
import { AnalyticsLayout } from "../Observe/AnalyticsLayout";
import { EditComment } from "../Observe/EditComment";
import { FullscreenLayout } from "../Observe/FullscreenLayout";
import useModuleMemory from "../Observe/hooks/UseModuleMemory";
import { IssueCommandPopup } from "../Observe/IssueCommandPopup";
import { Content, ModuleConfiguration } from "../Observe/ModuleConfiguration";
import { NewCommandPopup } from "../Observe/NewCommandPopup";
import { ObserveLayout } from "../Observe/ObserveLayout";
import { RecentEvents } from "../Observe/RecentEvents";
import { TeleopLayout } from "../Observe/TeleopLayout";
import { EditAnalyticsModule } from "../Settings/EditAnalyticsModule";
import { EditViewSidebar } from "../Settings/EditViewSidebar";
import { TrialBanner } from "../TrialBanner";
import { ViewMultiDeviceHeader } from "../ViewSmartFleet/ViewMultiDeviceHeader";
import { applyEventHashToRectangleStore } from "./applyEventHashToRectangleStore";
import { CarryV1TimeToCoherence } from "./CarryV1TimeToCoherence";
import * as styles from "./index.scss";
import {
    getHero,
    isFixed,
    moduleHasClearBackground,
    setHero,
    toggleClearBackground,
    toggleFixed,
} from "./ViewDevice.helpers";
import { ViewDeviceHeader } from "./ViewDeviceHeaders";
import { getV1TimeToCarryOver, ICarriedOverTime } from "./v1TimeCarryOver";

let timeout: Timeout | undefined;

export interface IViewDeviceProps {
    deviceId: Uuid | null;
    device: IDevice | null;
    deviceDetails: IDeviceDetails | null;
    deviceIds?: Uuid[];
    viewId: Uuid | undefined;
    view: IView | undefined;
    className?: string;
    onChangeViewId?: (newViewId: Uuid) => void;
    groupId?: Uuid;
    onChangeFocusedDevices?: (deviceIds: Uuid[]) => void;
    onChangeFilter?: (filter: IFilter) => void;
}

interface IStatelessViewDeviceState {
    isTeleopSession: boolean;
}

@observer
export class StatelessViewDevice extends Component<
    IViewDeviceProps,
    IStatelessViewDeviceState
> {
    @hashObservable()
    private filter: IFilter = {};
    @observable
    private tempView: IView | undefined;
    @observable
    private document?: IDeviceConfigurationDocument;
    @observable
    private version?: number;
    @observable
    private currentlyEditingNodeGraph: INodeGraph | null = null;
    @observable
    private isViewFocused: boolean = true;
    unsubscribeFromModeChange: any;

    private useModuleMemory = useModuleMemory();

    // eslint-disable-next-line
    declare context: ContextType<typeof FormantClientContext>;

    private checkTeleopStatusInterval: Timeout | undefined;

    private pendingCarriedOverTime: ICarriedOverTime | undefined;

    private getTitle = (): string | undefined => {
        const { groupId } = this.props;
        if (groupId) {
            return (groupStore.get(groupId) || undefined)?.name;
        }
    };

    constructor(props: IViewDeviceProps) {
        super(props);
        this.state = {
            isTeleopSession: false,
        };
    }

    private fetchDeviceName = async () => {
        const { deviceId, deviceIds } = this.props;
        if (!deviceId && !deviceIds) {
            return;
        }

        try {
            if (deviceId) {
                const fetchedDevice = await deviceStore.getAsync(deviceId);
                if (fetchedDevice) {
                    this.context.setDeviceName(fetchedDevice.name);
                    this.context.setDeviceId(fetchedDevice.id);
                }
            }
        } catch (error) {
            console.error("Failed to fetch device name:", error);
        }
    };

    public async componentDidMount() {
        this.unsubscribeFromModeChange = useBaseEditorStore(
            defaultStoreId,
        )?.subscribe(state => {
            observeStore.isCustomizing = state.mode === EditorMode.Edit;
        });

        if (this.props.deviceId) {
            await this.fetchDeviceConfig();
        }
        this.setTempViewAndFilter(this.props.view, true);
        window.addEventListener("popstate", this.handleBackNavigation);

        this.context.setViewId(this.props.viewId);
        this.context.setViewName(this.props.view?.name);
        this.context.setGroupName(this.getTitle());
        await this.fetchDeviceName();

        // If we arrived here via an event click (e.g. from DEVICE-EVENTS),
        // apply the hash parameters to the rectangle-sdk store so the device timeline seeks.
        applyEventHashToRectangleStore(
            window.location.hash,
            appStoreBase(defaultStoreId).getState(),
        );

        // Update tracking context
        setTrackingContext({
            viewId: this.props.viewId,
            viewName: this.props.view?.name,
            groupName: this.getTitle(),
            deviceName: this.props.device?.name,
            viewLayoutType:
                this.props.view?.layoutType === "dashboard" ? "v2" : "v1",
        });
    }

    public async componentWillUnmount() {
        window.removeEventListener("popstate", this.handleBackNavigation);
        if (this.unsubscribeFromModeChange) {
            this.unsubscribeFromModeChange();
        }
        if (this.checkTeleopStatusInterval) {
            clearInterval(this.checkTeleopStatusInterval);
            this.checkTeleopStatusInterval = undefined;
        }

        this.context.setViewId(undefined);
        this.context.setViewName(undefined);
        this.context.setGroupName(undefined);
    }

    private handleBackNavigation() {
        observeStore.isCustomizing = false;
    }

    public async componentDidUpdate(
        prevProps: Readonly<IViewDeviceProps>,
    ): Promise<void> {
        if (this.props.view?.id !== prevProps.view?.id) {
            this.pendingCarriedOverTime = getV1TimeToCarryOver(
                this.getLayoutType(prevProps.view),
                this.getLayoutType(),
                observeStore,
            );
        }

        const devicesAreDifferent =
            this.props.deviceIds &&
            prevProps.deviceIds &&
            (this.props.deviceIds.length !== prevProps.deviceIds.length ||
                this.props.deviceIds.some(
                    id => !prevProps.deviceIds?.includes(id),
                ));
        if (this.props.view?.id !== prevProps.view?.id || devicesAreDifferent) {
            this.setTempViewAndFilter(this.props.view);
        } else if (!equal(this.props.view, prevProps.view)) {
            const dirtyKeys = dirtyDiff(this.props.view, prevProps.view);
            if (dirtyKeys.includes("updatedAt") && this.tempView) {
                // we just stuff updatedAt into tempView so that we can not think it is dirty later
                this.tempView.updatedAt = this.props.view?.updatedAt;
            }
        }

        if (this.props.device) {
            if (this.props.device.id !== this.filter.deviceIds?.[0]) {
                this.onChangeFilter({
                    ...(this.props.view?.filter || {}),
                    deviceIds: [this.props.device.id || ""],
                });
            }
        } else if (
            JSON.stringify(this.props.deviceIds || []) !==
            JSON.stringify(prevProps.deviceIds || [])
        ) {
            this.onChangeFilter({
                ...(this.props.view?.filter || {}),
                deviceIds: this.props.deviceIds || [],
            });
        }

        if (prevProps.viewId !== this.props.viewId) {
            this.context.setViewId(this.props.viewId);
        }
        if (prevProps.view?.name !== this.props.view?.name) {
            this.context.setViewName(this.props.view?.name);
        }
        if (prevProps.groupId !== this.props.groupId) {
            this.context.setGroupName(this.getTitle());
        }

        // Update tracking context when any relevant prop changes
        if (
            prevProps.viewId !== this.props.viewId ||
            prevProps.view?.name !== this.props.view?.name ||
            prevProps.groupId !== this.props.groupId ||
            prevProps.device?.name !== this.props.device?.name
        ) {
            setTrackingContext({
                viewId: this.props.viewId,
                viewName: this.props.view?.name,
                groupName: this.getTitle(),
                deviceName: this.props.device?.name,
                viewLayoutType:
                    this.props.view?.layoutType === "dashboard" ? "v2" : "v1",
            });
        }

        const { deviceId, deviceIds } = this.props;
        if (
            deviceId !== prevProps.deviceId ||
            (deviceIds &&
                prevProps.deviceIds &&
                (deviceIds.length !== prevProps.deviceIds.length ||
                    deviceIds.some(id => !prevProps.deviceIds?.includes(id))))
        ) {
            await this.fetchDeviceName();
        }
    }

    public saveIfDirty = async () => {
        if (!equal(this.tempView, this.props.view)) {
            const dirtyKeys = dirtyDiff(
                this.props.view as Record<string, any>,
                this.tempView as Record<string, any>,
            );
            if (dirtyKeys.filter(k => k !== "updatedAt").length > 0) {
                await this.commitViewSave(this.tempView);
            }
        }
    };

    public saveIfNameDirty = async () => {
        if (!equal(this.tempView, this.props.view)) {
            const dirtyKeys = dirtyDiff(
                this.props.view as Record<string, any>,
                this.tempView as Record<string, any>,
            );
            if (
                dirtyKeys.includes("name") ||
                dirtyKeys.includes("description")
            ) {
                await this.commitViewSave(this.tempView);
            }
        }
    };

    public setTempViewAndFilter(
        tempView: IView | undefined,
        isInitialSetup: boolean = false,
    ) {
        if (tempView) {
            this.tempView = tempView;
            if (this.getLayoutType() === "dashboard" && isInitialSetup) {
                const defaultParams = UrlParamService.getParams();
                if (defaultParams.globalFilter) {
                    this.onChangeFilter(defaultParams.globalFilter as IFilter);
                    return;
                }
            }

            this.onChangeFilter(tempView.filter || {});
        }
    }

    private onEditView = (updates: Partial<IView>) => {
        const initial = this.tempView ?? this.props.view;
        if (initial) {
            this.setTempViewAndFilter({
                ...initial,
                ...updates,
            });
        }
    };

    private consumePendingCarriedOverTime = () => {
        const carried = this.pendingCarriedOverTime;
        this.pendingCarriedOverTime = undefined;
        return carried;
    };

    private onSelectEvent = (eventOrAggregate: IEventOrAggregate) => {
        const { event, aggregate } = eventOrAggregate;
        if (event) {
            observeStore.viewEvent(event);
        } else if (aggregate) {
            observeStore.showEventList(aggregate);
        }
    };

    public render() {
        const {
            deviceId,
            device: deviceItem,
            deviceDetails: deviceDetailsItem,
            className,
            viewId,
            view,
            deviceIds,
            groupId,
        } = this.props;

        const { editingComment } = observeStore;

        const {
            onChangeFilter,
            onFullscreen,
            onAnnotate,
            onChangeViewId,
            onSaveLayout,
        } = this;

        const filter = toJS(this.filter);

        const device = deviceItem || undefined;
        const deviceDetails = deviceDetailsItem || undefined;
        const id = deviceId || undefined;

        const layout = this.getLayout();
        if (!this.tempView || !layout) {
            return null;
        }

        const defaultParams = UrlParamService.getParams();

        const context: IFilter = {
            ...(toJS(this.tempView?.filter) || {}),
            deviceIds: id ? [id] : deviceIds || [],
        };

        const configuration: IViewConfiguration[] = view?.configuration || [];

        const layoutType = this.getLayoutType();

        // TODO: how to memo in Class components; consider moving up a level into Connected components
        // eslint-disable-next-line
        const viewContextModel: IViewContext = {
            view: this.tempView,
            context,
            deviceDetails: deviceDetails || undefined,
            viewId,
            layout: layout as ITeleopLayout,
            onRemoveModule: (moduleId: string) => {
                // if it is not a custom module, we need to take it out of the device filter
                appStore.setConfirm({
                    title: "Delete module",
                    message: `Are you sure you'd like to delete this module?`,
                    onOk: async () => {
                        let newFilter = this.filter;
                        if (!moduleId.match(/^custom-module:|^controls/)) {
                            newFilter = toggleStreamFromFilter(
                                moduleId,
                                newFilter,
                            );
                        }
                        const newLayout: any = { ...layout };
                        delete newLayout[moduleId];
                        track(
                            `${
                                LayoutName[layoutType || "observe"]
                            }: Remove Module`,
                        );
                        await this.onSaveLayout(newLayout, newFilter);
                    },
                });
            },
            onChangeLayout: this.onChangeLayout,
            onSaveLayout: this.onSaveLayout,
            onRevertChanges: this.onRevertChanges.bind(this),
            commitViewSave: this.commitViewSave.bind(this),
            onChangeViewId: this.onChangeViewId,
            currentlyEditingNodeGraph: this.currentlyEditingNodeGraph,
            setCurrentlyEditingNodeGraph: (graph: INodeGraph | null) => {
                this.currentlyEditingNodeGraph = graph;
            },
            isViewFocused: this.isViewFocused,
            setIsViewFocused: (isViewFocused: boolean) =>
                (this.isViewFocused = isViewFocused),
        };

        const hero = getHero(layout as ITeleopLayout);

        const isTeleop = layoutType === "teleop";
        const isAnalytics = layoutType === "analytics";

        const { isTeleopSession } = this.state;

        return (
            <ViewContext.Provider value={viewContextModel}>
                <div className={classnames(styles["view-device"], className)}>
                    {(!view ||
                        (view && (!view.url || view.showOnSingleDevice))) &&
                        deviceDetails && (
                            <>
                                <TrialBanner />
                                <DataRetentionBanner />
                                {(!observeStore.isCustomizing || isAnalytics) &&
                                    !isTeleopSession && (
                                        <ViewDeviceHeader
                                            view={
                                                observeStore.isCustomizing
                                                    ? this.tempView
                                                    : view
                                            }
                                            device={deviceDetails}
                                            viewId={viewId}
                                            filter={filter}
                                            context={context}
                                            onChangeViewId={onChangeViewId}
                                            onChangeFilter={onChangeFilter}
                                            onAnnotate={onAnnotate}
                                            onSaveLayout={onSaveLayout}
                                            layoutType={layoutType}
                                        />
                                    )}
                            </>
                        )}
                    {(!observeStore.isCustomizing || isAnalytics) &&
                        view &&
                        deviceIds &&
                        !deviceDetails &&
                        groupId && (
                            <ViewMultiDeviceHeader
                                deviceIds={deviceIds}
                                view={
                                    observeStore.isCustomizing
                                        ? this.tempView
                                        : view
                                }
                                groupId={groupId}
                                viewId={viewId}
                                filter={filter}
                                context={context}
                                onChangeFilter={this.onChangeFilter}
                                onChangeViewId={this.onChangeViewId}
                            />
                        )}
                    {view && layoutType === "fullscreen" && (
                        <FullscreenLayout
                            view={view}
                            context={context}
                            device={device}
                            layout={layout as IFullScreenLayout}
                            onSaveLayout={this.onSaveLayout}
                        />
                    )}
                    {view && isTeleop && id && (
                        <RestrictedComponent
                            resource="teleop"
                            requiredAccess="operator"
                        >
                            <TeleopLayout
                                id={id}
                                view={view}
                                context={context}
                                device={device}
                                onboarding={false}
                                layout={layout as ITeleopLayout}
                                onChangeLayout={this.onChangeLayout}
                            />
                        </RestrictedComponent>
                    )}

                    {view && layoutType === "dashboard" && (
                        <>
                            <UrlParamSyncHelper />
                            <Editor
                                device={device}
                                context={context}
                                filter={filter}
                                view={view as IView<"dashboard">}
                                hideEmbedModal={() => {
                                    observeStore.setShowEmbedModal(false);
                                }}
                                onPublish={() => {
                                    observeStore.setShowEmbedModal(true);
                                }}
                                editorConfig={{
                                    showEmbedModal: observeStore.showEmbedModal,
                                    hideHeader: false,
                                    realtimeV2Enabled:
                                        organizationStore.isRealtimeV2Enabled(),
                                    lockedMode:
                                        authStore.userRole &&
                                        authStore.hasAccessByRole(
                                            "administrator",
                                        )
                                            ? undefined
                                            : EditorMode.Read,
                                }}
                                inspectorConfig={{
                                    viewInspectorPanelProps: {
                                        localModeChangeEnabled: false,
                                        ARChangeEnabled:
                                            organizationStore.getAiEnabled(),
                                        VRChangeEnabled:
                                            organizationStore.getAiEnabled(),
                                    },
                                }}
                                panelView={this.tempView as IView<"dashboard">}
                                errorMessage={msg => {
                                    appStore.setSnackbar(msg);
                                }}
                                onChangeView={this.onEditView}
                                onChangeLayout={this.onChangeLayout}
                                currentlyEditingNodeGraph={
                                    this.currentlyEditingNodeGraph
                                }
                                rectangleConfig={{
                                    defaultParams,
                                }}
                            />
                            <CarryV1TimeToCoherence
                                consume={this.consumePendingCarriedOverTime}
                            />

                            <EventSidebarV2
                                context={context}
                                onDemandData={(time, endTime) => {
                                    appStore.onDemandData("", time, endTime);
                                }}
                                userId={authStore.userId || ""}
                                organizationId={authStore.organizationId || ""}
                            />
                        </>
                    )}

                    {view &&
                        (layoutType === "dashboard" ||
                            layoutType === "observe") && (
                            <RecentEvents
                                filter={context}
                                onSelect={this.onSelectEvent}
                            />
                        )}

                    {view && layoutType === "analytics" && (
                        <AnalyticsLayout
                            context={context}
                            device={device}
                            filter={filter}
                            layout={layout as IAnalyticsLayout}
                            onSaveLayout={this.onSaveLayoutSimple}
                            onChangeFilter={onChangeFilter}
                            configuration={configuration}
                            onRemoveModule={this.onRemoveSelfServiceModule}
                            onChangeLayout={this.onChangeLayout}
                        />
                    )}

                    {editingComment && (
                        <EditComment
                            comment={editingComment}
                            onSelectEvent={this.onSelectEvent}
                            context={context}
                            filter={filter}
                        />
                    )}
                    {view && layoutType === "observe" && (
                        <ObserveLayout
                            key={view.id}
                            context={context}
                            device={device}
                            filter={filter}
                            layout={layout as IObserveLayout}
                            onChangeLayout={this.onChangeLayout}
                            onSaveLayout={this.onSaveLayout}
                            configuration={configuration}
                            onRemoveModule={this.onRemoveSelfServiceModule}
                            commentEnabled={true}
                            annotateEnabled={authStore.featureEnabled(
                                "annotations",
                            )}
                            demandEnabled={authStore.featureEnabled("onDemand")}
                            hideDevice={true}
                            onChangeFilter={this.onChangeFilter}
                        />
                    )}

                    <RestrictedComponent
                        resource="commands"
                        requiredAccess="operator"
                    >
                        {appStore.commandTemplate && deviceDetails && (
                            <NewCommandPopup
                                device={deviceDetails}
                                commandTemplate={appStore.commandTemplate}
                            />
                        )}
                        {deviceStore.issuingCommand && id && (
                            <IssueCommandPopup deviceId={id} />
                        )}
                    </RestrictedComponent>
                    {view && layoutType !== "dashboard" && (
                        <Hotkey
                            code="shift+f"
                            label="Fullscreen"
                            onHotkey={onFullscreen}
                        />
                    )}
                </div>

                <Inspector
                    title="Customize"
                    onBack={async () => {
                        observeStore.configuringFlyout = undefined;
                        observeStore.isCustomizing = false;
                        await this.saveIfDirty();
                    }}
                    open={
                        observeStore.configuringFlyout !== undefined && isTeleop
                    }
                >
                    <Content>
                        <>
                            <Label
                                value="Module Translucency"
                                description="This sets the module's translucency"
                            />
                            <ListButton>
                                <Switch
                                    state={moduleHasClearBackground(
                                        layout,
                                        observeStore?.configuringFlyout?.[1]
                                            .streamName || "",
                                    )}
                                    onChange={() => {
                                        track(
                                            "Navigator: Customize: Toggle Translucency",
                                            {
                                                stream: observeStore
                                                    ?.configuringFlyout?.[1]
                                                    .streamName,
                                            },
                                        );
                                        toggleClearBackground(
                                            layout,
                                            observeStore?.configuringFlyout?.[1]
                                                .streamName || "",
                                            l => this.onChangeLayout(l),
                                        );
                                    }}
                                    label={
                                        moduleHasClearBackground(
                                            layout,
                                            observeStore?.configuringFlyout?.[1]
                                                .streamName || "",
                                        )
                                            ? "On"
                                            : "Off"
                                    }
                                />
                            </ListButton>
                            <Label
                                value="Fix Position"
                                description="This fixes the module in it's place"
                            />
                            <ListButton>
                                <Switch
                                    state={isFixed(
                                        layout,
                                        observeStore?.configuringFlyout?.[1]
                                            .streamName || "",
                                    )}
                                    onChange={async fixed => {
                                        track(
                                            "Navigator: Customize: Toggle Fixed Position",
                                            {
                                                stream: observeStore
                                                    ?.configuringFlyout?.[1]
                                                    .streamName,
                                            },
                                        );
                                        await toggleFixed(
                                            layout,
                                            observeStore?.configuringFlyout?.[1]
                                                .streamName || "",
                                            fixed,
                                            l => this.onChangeLayout(l),
                                        );
                                    }}
                                    label={
                                        isFixed(
                                            layout,
                                            observeStore?.configuringFlyout?.[1]
                                                .streamName || "",
                                        )
                                            ? "True"
                                            : "False"
                                    }
                                />
                            </ListButton>
                        </>
                        {!observeStore?.configuringFlyout?.[1].streamName.match(
                            /^custom|^controls/i,
                        ) && (
                            <>
                                <Label
                                    value="Set as Background Stream"
                                    description="This sets the hero module."
                                />
                                <ListButton>
                                    <Switch
                                        state={
                                            observeStore?.configuringFlyout?.[1]
                                                .streamName === hero
                                        }
                                        onChange={() => {
                                            track(
                                                "Navigator: Customize: Change Hero Stream",
                                                {
                                                    stream: observeStore
                                                        ?.configuringFlyout?.[1]
                                                        .streamName,
                                                },
                                            );

                                            const heroStream =
                                                observeStore
                                                    ?.configuringFlyout?.[1]
                                                    ?.streamName;
                                            if (heroStream) {
                                                setHero(
                                                    layout as ITeleopLayout,
                                                    heroStream || "",
                                                    l => this.onChangeLayout(l),
                                                );
                                            }
                                        }}
                                        label={
                                            observeStore?.configuringFlyout?.[1]
                                                .streamName ===
                                            getHero(layout as ITeleopLayout)
                                                ? "True"
                                                : "False"
                                        }
                                    />
                                </ListButton>{" "}
                            </>
                        )}
                    </Content>
                    {observeStore?.configuringFlyout?.[1] && (
                        <ModuleConfiguration
                            {...observeStore.configuringFlyout[1]}
                        />
                    )}
                </Inspector>

                <Inspector
                    title="Customize"
                    onBack={async () => {
                        if (id) {
                            observeStore.isCustomizing = false;
                            await this.saveIfDirty();
                            fork(this.saveDocument(id));
                        }
                    }}
                    open={
                        observeStore.isCustomizing &&
                        !observeStore?.configuringFlyout &&
                        isTeleop
                    }
                >
                    <EditViewSidebar
                        deviceId={id}
                        view={view}
                        onChangeView={this.onEditView}
                    />
                    {this.document?.teleop?.views && (
                        <Content>
                            <TeleopViewSelectField
                                key="view0"
                                deviceIds={device?.id ? [device?.id] : []}
                                document={this.document}
                                index={0}
                                view={this.document?.teleop?.views[0]}
                                label="Default Background Stream (HERO)"
                                onChangeView={(
                                    index: number,
                                    newView?: ITeleopViewConfiguration,
                                ) => {
                                    const currentHero = getHero(
                                        layout as ITeleopLayout,
                                    );
                                    if (currentHero) {
                                        setHero(
                                            layout as ITeleopLayout,
                                            currentHero,
                                            this.onSaveLayout.bind(this),
                                        );
                                    }
                                    this.onChangeView(index, newView);
                                }}
                            />
                        </Content>
                    )}
                </Inspector>

                {isAnalytics && (
                    <>
                        <Inspector
                            open={
                                !!observeStore.isCustomizing &&
                                !observeStore.configuringAnalyticsId
                            }
                            onBack={async () => {
                                if (view?.id) {
                                    observeStore.isCustomizing = false;
                                    this.onSimplySave({
                                        ...view,
                                        ...this.tempView,
                                    });
                                }
                            }}
                            showNewDesign={false}
                            deviceIds={context.deviceIds}
                        >
                            {!!observeStore.isCustomizing && (
                                <EditViewSidebar
                                    deviceId={id}
                                    view={this.tempView || view}
                                    onChangeView={this.onEditView}
                                    isV2Dashboard={false}
                                />
                            )}
                        </Inspector>

                        <Inspector
                            open={!!observeStore.configuringAnalyticsId}
                            onBack={this.saveAnalyticsLayout}
                            hotKeyExit={false}
                            title="Module"
                        >
                            <EditAnalyticsModule
                                moduleId={observeStore.configuringAnalyticsId}
                                layout={layout as IAnalyticsLayout}
                                onChangeLayout={this.onChangeLayout}
                            />
                        </Inspector>
                    </>
                )}
            </ViewContext.Provider>
        );
    }

    private saveDocument = async (id: string) => {
        if (!authStore.hasAccessByRole("administrator")) {
            return;
        }
        await deviceClient.updateConfiguration(await authStore.token(), id, {
            deviceId: id,
            document: defined(this.document),
        });
    };

    private onChangeView = (index: number, view?: ITeleopViewConfiguration) => {
        const { session } = operateStore;
        if (operateStore && session) {
            const teleop = session?.deviceConfiguration?.document.teleop || {};
            const views = session?.deviceConfiguration?.document.teleop
                ?.views || [{}, {}, {}, {}, {}];
            views[index] = view || {};

            if (operateStore?.session?.deviceConfiguration) {
                this.document = {
                    ...this.document,
                    teleop: {
                        ...teleop,
                        views,
                    },
                };
                operateStore.session.deviceConfiguration.document =
                    this.document;
            }
        }
    };

    public async tempSave(view: IView): Promise<void> {
        if (timeout) {
            clearTimeout(timeout);
            appStore.snackbar = undefined;
        }

        if (!authStore.hasAccessByRole("administrator")) {
            return;
        }

        const position: SnackbarPosition =
            view.layoutType !== "teleop" ? "top" : "side";

        appStore.snackbar = {
            message: "Save this layout?",
            actions: [
                {
                    label: "Save",
                    actionKey: "Enter",
                    onClick: async () => {
                        await viewStore.save(view);
                        track("Navigator: User Saved Changes");
                        appStore.snackbar = undefined;
                    },
                },
                {
                    label: "Revert",
                    onClick: () => {
                        track("Navigator: User Reverted Changes");
                        appStore.snackbar = undefined;
                        this.setTempViewAndFilter(this.props.view);
                    },
                },
            ],
            position,
        };

        timeout = setTimeout(() => {
            appStore.snackbar = undefined;
            timeout = undefined;
        }, 6000);
    }

    public async commitViewSave(view: IView | undefined): Promise<void> {
        if (view) {
            if (this.tempView?.layoutType === "dashboard") {
                // lets only update name so we don't overwrite layouts
                await viewStore.update(defined(view.id), {
                    name: view.name,
                    description: view.description,
                });
                return;
            }
            this.setTempViewAndFilter(view);
            if (this.tempView?.layoutType === "teleop") {
                return await this.tempSave(view);
            }
            await viewStore.save(view);
        }
    }

    private getLayoutType(view = this.props.view): LayoutType {
        let layoutType: LayoutType = "observe";
        if (
            (view && view.url && view.url.length > 0) ||
            view?.layoutType === "fullscreen"
        ) {
            layoutType = "fullscreen";
        } else if (
            view?.layoutType !== undefined &&
            view?.layoutType !== null
        ) {
            layoutType = view?.layoutType;
        }
        return layoutType;
    }

    private onChangeLayout = async (
        layout: IObserveLayout | IAnalyticsLayout | IDashboardLayout,
    ) => {
        if (
            this.getLayoutType() === "observe" ||
            this.getLayoutType() === "dashboard"
        ) {
            this.useModuleMemory.saveLayout(layout);
        }
        if (this?.tempView?.layout) {
            this.tempView.layout = layout;
        }
    };

    private getLayout():
        | IObserveLayout
        | IDashboardLayout
        | ITeleopLayout
        | IAnalyticsLayout
        | IFullScreenLayout
        | undefined {
        let layout = this.tempView?.layout;
        if (this.getLayoutType() === "observe") {
            const organizationId = authStore.organizationId;
            if (organizationId) {
                const organization = organizationStore.get(organizationId);
                if (organization && organization.betaUI) {
                    const localStorageLayout = this.useModuleMemory.getLayout();
                    if (localStorageLayout) {
                        layout = localStorageLayout;
                    }
                }
            }
        }
        return layout;
    }

    private onRevertChanges() {
        if (this.props.view) {
            this.setTempViewAndFilter(this.props.view);
        }
    }

    private onSimplySave(view: Partial<IView<keyof ILayoutTypeMap>>) {
        fork(
            (async () =>
                await viewStore.updateSimple(
                    defined(this.props.viewId),
                    view,
                ))().catch(error => {
                const responseError = narrowError(error, ResponseError);
                const { statusCode } = responseError;
                if (statusCode === 409) {
                    appStore.setSnackbar(
                        viewConflictMessage(
                            responseError,
                            "This view name already exists",
                        ),
                    );
                } else {
                    throw error;
                }
            }),
        );
    }

    private onSaveLayoutSimple = (
        layout: IObserveLayout | IFullScreenLayout | IAnalyticsLayout,
    ): void => {
        if (this.tempView) {
            return this.onSimplySave({ ...this.tempView, layout });
        }
    };

    private onSaveLayout = async (
        layout: IObserveLayout | IFullScreenLayout | IAnalyticsLayout,
        filter?: IFilter,
    ): Promise<void> => {
        const initial = this.tempView ?? this.props.view;
        if (initial) {
            const filterSource =
                filter || this.tempView?.filter || this.props.view?.filter;
            const newView = {
                ...initial,
                filter: { ...filterSource },
                layout,
            };
            await this.commitViewSave(newView);
        }
    };

    private onChangeViewId = async (viewId: Uuid) => {
        const { deviceId: id, deviceIds, onChangeViewId } = this.props;
        const { navigate, routeTo } = routerStore;
        track("View Changed");
        if (id) {
            await navigate(
                routeTo.unifiedDevice({ mode: "views", id, viewId }),
            );
        }
        if (!id && deviceIds && onChangeViewId) {
            onChangeViewId(viewId);
        }
    };

    private onChangeFilter = (newFilter: IFilter) => {
        this.filter = newFilter;
        if (this.props.onChangeFilter) {
            this.props.onChangeFilter(toJS(newFilter));
        }
        const { onChangeFocusedDevices, deviceIds } = this.props;
        if (onChangeFocusedDevices) {
            onChangeFocusedDevices(newFilter.deviceIds || deviceIds || []);
        }
    };

    private onFullscreen = () => {
        observeStore.toggleFullscreen();
    };

    private onAnnotate = (): void => {
        if (!this.props.deviceId) {
            return;
        }
        const annotation = { deviceId: this.props.deviceId };
        if (this.getLayoutType() === "dashboard") {
            const baseStore = appStoreBase(defaultStoreId);
            baseStore.getState().setEventSidebarIsOpen(true);
            baseStore.getState().setEditingAnnotation({
                ...annotation,
                type: "annotation",
                message: "",
                time: new Date().toISOString(),
                userId: defined(authStore.userId),
                annotationTemplateId: "",
                tags: {},
                streamName: undefined,
                streamType: undefined,
            } as IAnnotation);
        } else {
            observeStore.editAnnotation(annotation);
        }
    };

    async removeStream(deviceId: Uuid, streamName: string) {
        const device = await deviceStore.fetchGet(deviceId);
        if (!device) {
            throw new Error("Device not found");
        }
        if (!device.desiredConfigurationVersion) {
            throw new Error("Device configuration version not found");
        }
        const configuration = await deviceConfigurationStore.fetchGet(
            defined(device.id),
            device.desiredConfigurationVersion,
        );
        const { document: oldDocument } = configuration;
        const { telemetry = {} } = oldDocument;
        const oldStreams = telemetry.streams ?? [];
        const streams = oldStreams.filter(_ => _.name !== streamName);

        const document = {
            ...oldDocument,
            telemetry: {
                ...telemetry,
                streams,
            },
        };

        await deviceClient.updateConfiguration(
            await authStore.token(),
            deviceId,
            { deviceId, document },
        );
    }

    private onRemoveSelfServiceModule = async (streamName: string) => {
        const { viewId, deviceId } = this.props;
        const view = viewId ? !!viewId && viewStore.get(viewId) : false;
        if (!view || !deviceId) {
            return;
        }
        if (!streamName.startsWith("custom-module:")) {
            await this.removeStream(deviceId, streamName);
        }
        await SelfServiceViewUpdater.persistView(
            SelfServiceViewUpdater.removeModules(view, streamName),
            { appStore, observeStore, viewStore },
            { skipNextLayout: true },
        );
    };

    private fetchDeviceConfig = async () => {
        const { deviceId: id } = this.props;
        const { version: oldVersion } = this;
        if (!id) {
            return;
        }

        const device = await deviceStore.fetchGet(id);
        if (device === false) {
            return;
        }

        const newVersion = device.desiredConfigurationVersion;
        if (!newVersion) {
            return;
        }

        if (oldVersion === newVersion) {
            return;
        }

        const configuration = await deviceClient.getConfiguration(
            await authStore.token(),
            id,
            newVersion,
        );

        this.document = configuration.document;
        this.version = newVersion;
    };

    private saveAnalyticsLayout = async () => {
        if (this.tempView === undefined || !this.tempView?.layout) {
            return;
        }
        const layout = this.getLayout();
        const currentLayout = layout as IAnalyticsLayout;

        if (currentLayout && observeStore.configuringAnalyticsId) {
            this.onSimplySave({ layout: currentLayout });
        }
        observeStore.configuringAnalyticsId = undefined;
        observeStore.isCustomizing = false;
        observeStore.fullscreen = false;
    };
}

StatelessViewDevice.contextType = FormantClientContext;
